/* eslint-disable jsdoc/require-returns */
import getDOMfromString from '../../helpers/getDOMfromString';
import isMobile from '../../helpers/isMobile';
import tabTrap from '../../helpers/tabTrap';
import triggerExploreScroll from '../../helpers/triggerExploreScroll';
import triggerEditorialScroll from '../../helpers/triggerEditorialScroll';
import {gsap} from '../../vendors/gsap';

import './chapters.css';

/**
 *
 * @param {Array} $items List items
 * @param {HTMLElement} $control Control element
 */
const handleResize = ($items, $control) => {
    window.requestAnimationFrame(() => {
        const controlX = Number(window.innerWidth - $items[0].getBoundingClientRect().x - $control.offsetWidth) ?? 0;
        $control.style.right = `${controlX}px`;
    });
};

/**
 *
 * @param {Array<HTMLElement>} $items The list of items
 * @returns {number} The total width calculated based on the items
 */
const getListOffsetPos = $items => {
    let totalWidth = window.innerWidth >= 1024 && $items?.length ? 18 : 0;

    $items?.forEach((item, index) => {
        if (window.innerWidth >= 1024 && (($items.length > 6 && index < 2) || ($items.length > 2 && !index))) {
            totalWidth += item.offsetWidth;
        } else if (window.innerWidth < 1024 && index < $items.length - 1) {
            totalWidth += item.offsetWidth;
        }
    });

    return totalWidth;
};

/**
 * Calculates and returns the amount by which chapters menu should be translated in x direction
 *
 * @param {number} $index - clicked item index
 * @param {Array} $items List items
 * @returns {number} - value to translate
 */
const getTranslateValue = ($index, $items) => {
    const validItems = Array.from($items).slice($index + 1);
    return validItems.reduce((accumulator, item) => accumulator + item.getBoundingClientRect().width, 0);
};

/**
 * Chapters
 *
 * @returns {void}
 */
const chapters = () => {
    const $chaptersMenu = document.querySelector('.app-chapters-menu');
    const $items = $chaptersMenu?.querySelectorAll('a');
    const $backdrop = $chaptersMenu?.querySelector('.backdrop');
    const $control = $chaptersMenu?.querySelector('.control');
    const $toggle = $chaptersMenu?.querySelector('.toggle');
    const $seeAllChaptersBtn = document.querySelector('.next-chapter button');
    const $hamburger = document.querySelector('[data-action=app-submenu-hamburger]');
    const $chaptersLink = document.querySelector('[data-action=chapters]');
    const $mainContent = document.querySelector('.app-content');
    const $mainContainer = document.querySelector('.app-container');

    const tabs =
        'a:not(.header a):not(.app-chapters-menu a):not(.timeline-container a):not(.UsableNetAssistive), button:not(.hamburger):not(.app-chapters-menu .toggle):not([data-action="chapters"]), .swiper-button-next, .swiper-button-prev';
    const mm = gsap.matchMedia();
    let tl = gsap.timeline();
    let tlClick = gsap.timeline({defaults: {force3D: true}});
    let toggleToCollapsed = false;

    if ($items && $items.length > 0) {
        tl = gsap.timeline({
            paused: true,
            defaults: {force3D: true},
            /**
             *
             */
            onUpdate: () => {
                handleResize($items, $control);
            },
            /**
             * start callback
             */
            onStart: () => {
                $chaptersMenu.removeAttribute('aria-hidden');
                $chaptersMenu.removeAttribute('tabindex');
                $toggle.removeAttribute('tabindex');
                $chaptersMenu.classList.remove('!invisible');
            }
        });

        gsap.set('.app-chapters-menu nav', {marginRight: `-${getListOffsetPos($items)}`});

        mm.add('(max-width: 1023px)', () => {
            if (tl) tl.progress(0).kill(true);

            tl.add('start')
                .to(
                    '.app-chapters-menu',
                    {
                        x: '100%',
                        duration: 0.4,
                        ease: 'sine.in'
                    },
                    0
                )
                .add('invisible')
                .to(
                    '.app-chapters-menu',
                    {
                        x: 0,
                        duration: 0.4,
                        ease: 'sine.in',
                        /**
                         * Handles actions to be performed when the timeline start its animation.
                         */
                        onStart: () => {
                            $chaptersMenu.classList.add('visible');
                        }
                    },
                    0.4
                )
                .add('visible')
                .to(
                    '.app-chapters-menu .backdrop',
                    {
                        opacity: 1,
                        duration: 0.4,
                        ease: 'none',
                        /**
                         * Handles actions to be performed when the timeline start its animation.
                         */
                        onStart: () => {
                            $chaptersMenu.classList.add('expanded');
                            $chaptersMenu.setAttribute('aria-modal', 'true');
                            $toggle.setAttribute('aria-expanded', 'true');
                            $chaptersLink.setAttribute('aria-expanded', 'true');
                            $seeAllChaptersBtn?.setAttribute('aria-expanded', 'true');
                            $items.forEach(item => item.removeAttribute('tabindex'));
                            tabTrap.enable(tabs);
                            triggerExploreScroll(true);
                            triggerEditorialScroll(true);
                            tabTrap.enable('.skip-link,.header,.app-content,.app-submenu,.js-onboarding-wizard');
                            $toggle.focus();
                        },
                        /**
                         * on complete callback
                         */
                        onComplete: () => {
                            $mainContent.classList.add('hidden');
                            if ($mainContainer !== null) {
                                $mainContainer.classList.add('m-without_header');
                            }
                        },
                        /**
                         * Handles actions to be performed when the timeline ends its animation.
                         */
                        onReverseComplete: () => {
                            $chaptersMenu.classList.remove('expanded');
                            $chaptersMenu.setAttribute('aria-modal', 'false');
                            $toggle.setAttribute('aria-expanded', 'false');
                            $chaptersLink.setAttribute('aria-expanded', 'false');
                            $seeAllChaptersBtn?.setAttribute('aria-expanded', 'false');
                            if ($mainContainer !== null) {
                                $mainContainer.classList.remove('m-without_header');
                            }
                            tabTrap.disable(tabs);
                            if (!$hamburger.classList.contains('active')) {
                                triggerExploreScroll(false);
                                triggerEditorialScroll(false);

                                // accessiblity
                                tabTrap.disable('.skip-link,.header,.app-content,.app-submenu,.js-onboarding-wizard');
                            } else {
                                tabTrap.disable('.skip-link,.header,.app-submenu');
                            }
                        }
                    },
                    0.8
                )
                .to(
                    '.app-chapters-menu nav',
                    {
                        marginRight: 0,
                        duration: 0.4,
                        ease: 'sine.in'
                    },
                    0.8
                )
                .to(
                    '.app-chapters-menu nav .list a',
                    {
                        x: 0,
                        duration: 0.6,
                        ease: 'power2.inOut',
                        stagger: 0.1
                    },
                    0.9
                )
                .to(
                    '.app-chapters-menu .toggle',
                    {
                        x: `-${$toggle.clientWidth * 2 + 2 ?? 0}`,
                        rotateY: '180deg',
                        duration: 0.6,
                        ease: 'circ.inOut'
                    },
                    1
                )
                .add('end');
        });

        mm.add('(min-width: 1024px)', () => {
            if (tl) tl.progress(0).kill(true);

            tl.add('start')
                .to(
                    '.app-chapters-menu',
                    {
                        x: '100%',
                        duration: 0.4,
                        ease: 'sine.in'
                    },
                    0
                )
                .add('invisible')
                .to(
                    '.app-chapters-menu',
                    {
                        x: 0,
                        duration: 0.4,
                        ease: 'sine.in',
                        /**
                         * Handles actions to be performed when the timeline start its animation.
                         */
                        onStart: () => {
                            $chaptersMenu.classList.add('visible');
                        }
                    },
                    0.4
                )
                .add('visible')
                .to(
                    '.app-chapters-menu .backdrop',
                    {
                        opacity: 1,
                        duration: 0.4,
                        ease: 'sine.in',
                        /**
                         * Handles actions to be performed when the timeline start its animation.
                         */
                        onStart: () => {
                            $chaptersMenu.classList.add('expanded');
                            $chaptersMenu.setAttribute('aria-modal', 'true');
                            $toggle.setAttribute('aria-expanded', 'true');
                            $chaptersLink.setAttribute('aria-expanded', 'true');
                            $seeAllChaptersBtn?.setAttribute('aria-expanded', 'true');
                            $items.forEach(item => item.removeAttribute('tabindex'));
                            tabTrap.enable(tabs);
                            triggerExploreScroll(true);
                            triggerEditorialScroll(true);
                            tabTrap.enable('.skip-link,.app-content,.app-submenu,.js-onboarding-wizard');
                            $toggle.focus();
                        },
                        /**
                         * on complete callback
                         */
                        onComplete: () => {
                            $mainContent.classList.add('hidden');
                        },
                        /**
                         * Handles actions to be performed when the timeline ends its animation.
                         */
                        onReverseComplete: () => {
                            $chaptersMenu.classList.remove('expanded');
                            $chaptersMenu.setAttribute('aria-modal', 'false');
                            $toggle.setAttribute('aria-expanded', 'false');
                            $chaptersLink.setAttribute('aria-expanded', 'false');
                            $seeAllChaptersBtn?.setAttribute('aria-expanded', 'false');
                            tabTrap.disable(tabs);
                            if (!$hamburger.classList.contains('active')) {
                                triggerExploreScroll(false);
                                triggerEditorialScroll(false);

                                // accessibility
                                tabTrap.disable('.skip-link,.header,.app-content,.app-submenu,.js-onboarding-wizard');
                            } else {
                                tabTrap.disable('.skip-link,.header,.app-submenu');
                            }
                        }
                    },
                    0.8
                )
                .to(
                    '.app-chapters-menu nav',
                    {
                        marginRight: 0,
                        duration: 0.6,
                        ease: 'sine.in'
                    },
                    0.8
                )
                .to(
                    '.app-chapters-menu nav .list a',
                    {
                        /**
                         * Get padding left dynamically
                         */
                        paddingLeft: () => window.innerWidth * 0.08,
                        duration: 0.6,
                        ease: 'sine.inOut',
                        stagger: 0.05
                    },
                    1
                )
                .to(
                    '.app-chapters-menu nav .list li:nth-child(2) span.line, .app-chapters-menu nav .list li:nth-child(3) span.line',
                    {
                        height: '100%',
                        duration: 0.6,
                        ease: 'circ.inOut',
                        stagger: 0.1
                    },
                    1.2
                )
                .to(
                    '.app-chapters-menu .toggle',
                    {
                        x: `-${$toggle.clientWidth * 2 + 2 ?? 0}`,
                        rotateY: '180deg',
                        duration: 0.6,
                        ease: 'circ.inOut'
                    },
                    1.4
                )
                .add('end');
        });
    }

    /**
     * Hides the chapters menu
     *
     * @param {CustomEvent} detail Custom event containing details property to also hide after the close animation is done
     */
    const toggleShowHide = detail => {
        const isExpanded = $chaptersMenu.classList.contains('expanded');
        const isVisible = $chaptersMenu.classList.contains('visible');
        const show = detail?.chaptersMenu ?? false;
        const expand = detail?.chaptersMenuExpand ?? false;
        toggleToCollapsed = detail?.toggleToCollapsed ?? false;

        if (isExpanded) {
            document.querySelector('.app-content').classList.remove('hidden');
            $items.forEach(item => item.setAttribute('tabindex', '-1'));
        }

        if (show && !isExpanded) {
            tl.tweenFromTo(isVisible ? 'visible' : 'start', expand ? 'end' : 'visible', {
                /**
                 *
                 */
                onStart: () => {
                    gsap.set('.app-chapters-menu nav .list li:first-child span.line', {
                        height: isMobile() && toggleToCollapsed ? $chaptersMenu.offsetHeight - 56 : '100%'
                    });
                }
            });
        } else if (isVisible) {
            tl.tweenFromTo(isExpanded ? 'end' : 'visible', toggleToCollapsed ? 'visible' : 'invisible', {
                /**
                 *
                 */
                onComplete: () => {
                    if (!toggleToCollapsed) {
                        $chaptersMenu.classList.remove('visible', 'expanded');
                        $chaptersMenu.classList.add('!invisible');
                        $chaptersMenu.setAttribute('aria-hidden', 'true');
                        $chaptersMenu.setAttribute('tabindex', '-1');
                        $toggle.setAttribute('tabindex', '-1');
                        $items.forEach(item => item.setAttribute('tabindex', '-1'));
                    } else {
                        $chaptersMenu.classList.remove('expanded');
                    }

                    $toggle.setAttribute('aria-expanded', 'false');
                    $chaptersLink.setAttribute('aria-expanded', 'false');
                    $seeAllChaptersBtn?.setAttribute('aria-expanded', 'false');
                }
            });
        }
    };

    /**
     * Close chapters menu
     */
    const handleClose = () => {
        toggleShowHide({
            chaptersMenu: false,
            chaptersMenuExpand: false,
            toggleToCollapsed
        });
    };

    /**
     * Toggle open and close chapters menu
     */
    const handleToggleExpand = () => {
        const isExpanded = $chaptersMenu.classList.contains('expanded');

        const toggleState = {
            chaptersMenu: !isExpanded,
            chaptersMenuExpand: !isExpanded,
            toggleToCollapsed
        };

        toggleShowHide(toggleState);

        const toggleChaptersEvent = new CustomEvent('250.chapters.toggle', {
            bubbles: true,
            cancelable: false,
            detail: toggleState
        });

        window.dispatchEvent(toggleChaptersEvent);
    };

    /**
     * Handles chapters menu link click
     *
     * @param {Event} e - click event
     */
    const handleMenuClick = e => {
        e.preventDefault();

        if (tl.isActive() || tlClick.isActive()) return;

        const isExpanded = $chaptersMenu.classList.contains('expanded');

        // Toggle aria-expanded of the chapters menu button
        const chaptersBtn = document.querySelector('[data-action=chapters]');
        if (chaptersBtn) {
            chaptersBtn.ariaExpanded = !isExpanded;
        }

        if (!isExpanded) {
            toggleShowHide({
                chaptersMenu: true,
                chaptersMenuExpand: true,
                toggleToCollapsed
            });
            return;
        }

        document.querySelector('.app-content').classList.remove('hidden');
        document.querySelector('body').focus();

        let target = e.target;

        if (target.tagName.toLowerCase() === 'a' || target.closest('a')) {
            target = target.tagName.toLowerCase() === 'a' ? target : target.closest('a');
            const parent = target.parentElement;
            const index = Number(target.dataset.itemIndex);
            const menuLabel = target.dataset.itemValue;

            // remove and add chapter menu details view div to menu
            document.querySelector('.chapter-details-view')?.remove();
            parent.prepend(
                getDOMfromString(`
                    <div class="chapter-details-view">
                        <div class="chapter-heading-container">
                            <h1 class="chapter-heading">${menuLabel}</h1>
                            <div class="chapter-heading-overlay"></div>
                        </div>
                    </div>
                `)
            );

            if (tlClick) tlClick.progress(0).kill(true);

            // define animation - defining here because of dynamically adding chapter details view
            tlClick = gsap.timeline({
                defaults: {force3D: true},
                /**
                 *
                 */
                onUpdate: () => {
                    handleResize($items, $control);
                },
                /**
                 * Handles actions to be performed when the timeline completes its animation.
                 */
                onComplete: () => {
                    document.querySelector('.chapter-details-view')?.remove();
                    $chaptersMenu.classList.remove('visible', 'expanded');
                    $chaptersMenu.classList.add('!invisible');
                    $chaptersMenu.setAttribute('aria-hidden', 'true');
                    $toggle.setAttribute('aria-expanded', 'false');
                    $chaptersLink.setAttribute('aria-expanded', 'false');
                    $seeAllChaptersBtn?.setAttribute('aria-expanded', 'false');
                    tl.progress(0).kill(true);
                    tlClick.progress(0).kill(true);
                }
            });

            tlClick
                .fromTo(
                    '.chapter-details-view',
                    {
                        autoAlpha: 0
                    },
                    {
                        autoAlpha: 1,
                        duration: 0.2,
                        ease: 'power1.in'
                    }
                )
                .fromTo(
                    '.chapter-details-view',
                    {
                        width: 0
                    },
                    {
                        width: '100vw',
                        duration: 1.2,
                        ease: 'expo.out'
                    }
                )
                .fromTo(
                    '.app-chapters-menu nav',
                    {
                        x: 0
                    },
                    {
                        x: getTranslateValue(index, $items),
                        duration: 1.2,
                        ease: 'expo.out'
                    },
                    '<'
                )
                .fromTo(
                    '.chapter-heading-container',
                    {
                        scale: 1.4,
                        x: '30%'
                    },
                    {
                        scale: 1,
                        x: 0,
                        duration: 1.2,
                        ease: 'power2.out'
                    },
                    '<+=0.3'
                )
                .fromTo(
                    '.chapter-heading-overlay',
                    {
                        y: '0%'
                    },
                    {
                        y: '-100%',
                        duration: 0.6,
                        ease: 'power2.out'
                    },
                    '<+=0.3'
                )
                .to(
                    '.chapter-heading-container',
                    {
                        scale: 0.6,
                        duration: 0.6,
                        ease: 'power2.out'
                    },
                    '+=1'
                )
                .to(
                    '.chapter-heading-overlay',
                    {
                        y: 0,
                        duration: 0.6,
                        ease: 'power2.out'
                    },
                    '<'
                );
        }
    };

    /**
     * Event listener to updates chapters menu control position on window resize
     */
    const onWindowResize = () => {
        window.addEventListener('resize', () => handleResize($items, $control));
    };

    /**
     * Event listener to toggles chapters menu
     */
    const onToggleClick = () => {
        $toggle.addEventListener('click', handleToggleExpand);
    };

    /**
     * Event listener to xlose chapters menu when clicking on the backdrop
     */
    const onBackdropClick = () => {
        $backdrop.addEventListener('click', handleClose);
    };

    /**
     * Event listener to handle chapters menu link click
     */
    const onMenuClick = () => {
        $chaptersMenu.addEventListener('click', handleMenuClick);
    };

    /**
     * Initial data setup and event listeners
     */
    const init = () => {
        if ($items && $items.length > 0) {
            onWindowResize();
            onToggleClick();
            onBackdropClick();
            onMenuClick();

            window.addEventListener('250.chapters', e => toggleShowHide(e.detail));
        }
    };

    return {
        init,
        close: handleClose
    };
};

export default chapters();
